import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import InputPane from "./workbench/InputPane";
import WorkbenchPane from "./workbench/WorkbenchPane";
import ClientePane from "./workbench/ClientePane";
import QuickMessageModal from "./workbench/QuickMessageModal";
import MobileGate from "../components/MobileGate";
import NormalizationConfirmModal from "../components/NormalizationConfirmModal";
import BlocchiClienteBanner from "../components/BlocchiClienteBanner";
import SezioneSpedizione from "../components/SezioneSpedizione";
import useNormalizationQueue from "../hooks/useNormalizationQueue";
import { api, formatErrorDetail } from "../lib/api";

export default function Workbench() {
  const [session, setSession] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [matching, setMatching] = useState(false);
  const [analyzeError, setAnalyzeError] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [generatingMsg, setGeneratingMsg] = useState(false);
  const [messageError, setMessageError] = useState("");
  const [blocchi, setBlocchi] = useState([]);
  const [searchParams] = useSearchParams();

  const normQueue = useNormalizationQueue(session);

  // Auto-clear analyze error after a while
  useEffect(() => {
    if (!analyzeError) return;
    const t = setTimeout(() => setAnalyzeError(""), 8000);
    return () => clearTimeout(t);
  }, [analyzeError]);

  // Auto-load session from ?session=<id> in URL
  useEffect(() => {
    const sid = searchParams.get("session");
    if (sid && (!session || session.session_id !== sid)) {
      handleOpenSession(sid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const notify = (msg, opts = {}) => toast(msg, { duration: 3000, ...opts });

  /** Resolve domain for preferences lookup */
  const inferDomain = (cliente) => {
    if (!cliente) return null;
    if (cliente.email_domain) return cliente.email_domain;
    if (cliente.email && cliente.email.includes("@"))
      return cliente.email.split("@").pop();
    return null;
  };

  const handleAnalyze = async (text, files) => {
    setAnalyzeError("");
    setAnalyzing(true);
    setSession(null);
    setBlocchi([]);
    try {
      let data = await api.analyze(text, files);
      const domain = inferDomain(data?.cliente);
      if (domain) {
        // R5 P1 #3 — extended pre-fill from customer preferences
        try {
          const plan = await api.preferencesApply(domain);
          // Certifications → each voce
          if (plan.certs?.length && data.voci?.length) {
            data.voci.forEach((v) => {
              v.parametri_inferiti = v.parametri_inferiti || {};
              if (!v.parametri_inferiti.P22_certificazioni) {
                v.parametri_inferiti.P22_certificazioni = plan.certs;
              }
            });
            toast.info(
              `${plan.certs.length} preferenze certificazioni applicate dal profilo cliente`
            );
          }
          // Shipping patch (Consegna)
          if (plan.shipping_patch && Object.keys(plan.shipping_patch).length) {
            data.shipping = { ...(data.shipping || {}), ...plan.shipping_patch };
            toast.info("Zona spedizione pre-compilata dalle preferenze cliente");
          }
          // Contatto override → email
          if (plan.contatto_override?.email) {
            data.cliente = data.cliente || {};
            if (!data.cliente.email) {
              data.cliente.email = plan.contatto_override.email;
            }
          }
          // Blocchi banner
          if (plan.blocchi?.length) {
            setBlocchi(plan.blocchi);
          }
        } catch {
          /* silent — preferences pre-fill is best-effort */
        }
      }
      setSession(data);
      // R10 v0.5.8 — Async path: PDF Vision multipage processato in background.
      // Polla finché lo status passa da "analyzing" a matching/awaiting_info/error.
      if (data?._async || data?.status === "analyzing") {
        toast.info(
          "PDF in lettura — può richiedere fino a 90s per scansioni multipagina…",
          { duration: 8000 }
        );
        const sid = data.session_id;
        let polled = data;
        for (let i = 0; i < 60; i++) {
          // 60 × 2s = 120s max wait
          await new Promise((r) => setTimeout(r, 2000));
          try {
            polled = await api.getSession(sid);
          } catch {
            continue;
          }
          if (polled?.status && polled.status !== "analyzing") {
            break;
          }
        }
        if (polled?.status === "error") {
          setAnalyzeError(
            polled.extraction_error ||
              "Estrazione PDF non riuscita — riprova o usa il testo."
          );
          setAnalyzing(false);
          return;
        }
        setSession(polled);
        data = polled;
      }
      // R10 v0.5.8 — toast OCR feedback per PDF/immagini
      const vMeta = data?.vision_meta || {};
      const fmtUsed = vMeta.format_used;
      const excluded = (vMeta.voci_escluse_per_cancellazione || []).length;
      const voci = (data?.voci || []).length;
      if (fmtUsed === "pdf_scanned" || fmtUsed === "image") {
        const noun = fmtUsed === "image" ? "Immagine letta" : "PDF letto";
        if (excluded > 0) {
          toast.success(
            `${noun}: ${voci} voc${voci === 1 ? "e" : "i"} estratt${
              voci === 1 ? "a" : "e"
            }, ${excluded} escluse perché barrate dal cliente`
          );
        } else {
          toast.success(
            `${noun}: ${voci} voc${voci === 1 ? "e" : "i"} estratt${
              voci === 1 ? "a" : "e"
            }`
          );
        }
      } else if (fmtUsed === "pdf_native") {
        toast.success(`PDF letto (testo): ${voci} voc${voci === 1 ? "e" : "i"} estratt${voci === 1 ? "a" : "e"}`);
      }
      const hasMissing = (data.voci || []).some(
        (v) => (v.parametri_mancanti_critici || []).length > 0
      );
      if (hasMissing && data.session_id) {
        try {
          const r = await api.quickMessage(data.session_id);
          if (r?.message) {
            setSession((s) => ({ ...s, quick_message: r.message }));
          }
        } catch {
          /* silent */
        }
      }
    } catch (e) {
      setAnalyzeError(formatErrorDetail(e));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleOpenSession = async (sid) => {
    setAnalyzeError("");
    setAnalyzing(true);
    try {
      const data = await api.getSession(sid);
      setSession(data);
      // also refresh blocchi from customer
      const domain = inferDomain(data?.cliente);
      if (domain) {
        try {
          const plan = await api.preferencesApply(domain);
          setBlocchi(plan.blocchi || []);
        } catch {
          setBlocchi([]);
        }
      } else {
        setBlocchi([]);
      }
    } catch (e) {
      setAnalyzeError(formatErrorDetail(e));
    } finally {
      setAnalyzing(false);
    }
  };

  const handlePatch = async (patch) => {
    if (!session) return;
    setSaving(true);
    try {
      const updated = await api.patchSession(session.session_id, patch);
      setSession(updated);
    } catch (e) {
      setAnalyzeError(formatErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  const handleMatch = async () => {
    if (!session) return;
    setMatching(true);
    try {
      const updated = await api.match(session.session_id);
      setSession(updated);
    } catch (e) {
      setAnalyzeError(formatErrorDetail(e));
    } finally {
      setMatching(false);
    }
  };

  const handleGenerateMessage = async (openModal = true) => {
    if (!session) return;
    setMessageError("");
    setGeneratingMsg(true);
    if (openModal) setMessageOpen(true);
    try {
      await api.patchSession(session.session_id, { voci: session.voci });
      const r = await api.quickMessage(session.session_id);
      if (r?.no_missing) {
        toast.info("Tutte le info necessarie sono già presenti.");
        setSession((s) => ({ ...s, quick_message: "" }));
      } else {
        setSession((s) => ({ ...s, quick_message: r.message }));
        toast.success("Messaggio rapido generato");
      }
    } catch (e) {
      const detail = formatErrorDetail(e);
      setMessageError(detail);
      toast.error(detail || "Errore generazione messaggio");
    } finally {
      setGeneratingMsg(false);
    }
  };

  /** R5 — merge an inference-confirm response back into session */
  const applyInferenceResolved = ({ updatedVoce }) => {
    if (!updatedVoce || !session) return;
    setSession((s) => ({
      ...s,
      voci: (s.voci || []).map((v, i) =>
        i === updatedVoce.idx ? { ...v, ...updatedVoce } : v
      ),
    }));
    normQueue.advance();
  };

  const closeSession = () => {
    setSession(null);
    setAnalyzeError("");
    setBlocchi([]);
  };

  return (
    <MobileGate>
      <div
        className="grid grid-cols-[22%_62%_16%] h-[calc(100vh-3.5rem)] min-w-[1280px] overflow-x-hidden"
        data-testid="workbench-layout"
      >
        <InputPane
          onAnalyze={handleAnalyze}
          onOpenSession={handleOpenSession}
          loading={analyzing}
          error={analyzeError}
          channel={session?.channel}
          sessionId={session?.session_id}
          initialChecklist={session?.verification_checklist}
          onAddVoce={async (voce) => {
            if (!session?.session_id) return;
            const nextIdx = (session.voci || []).length;
            const newVoci = [
              ...(session.voci || []),
              { ...voce, idx: nextIdx },
            ];
            await handlePatch({ voci: newVoci });
          }}
          shippingNode={
            session?.session_id ? (
              <SezioneSpedizione
                sessionId={session.session_id}
                session={session}
                onUpdate={({ shipping, margin_calc }) =>
                  setSession((s) => ({
                    ...s,
                    shipping: shipping ?? s.shipping,
                    margin_calc: margin_calc ?? s.margin_calc,
                  }))
                }
              />
            ) : null
          }
        />
        <WorkbenchPane
          session={session}
          loading={analyzing}
          saving={saving}
          matching={matching}
          blocchi={blocchi}
          onClose={closeSession}
          onPatch={handlePatch}
          onMatch={handleMatch}
          onOpenMessage={() => handleGenerateMessage(true)}
          onReloadSession={() =>
            session?.session_id && handleOpenSession(session.session_id)
          }
          onVociUpdate={(voci) =>
            setSession((s) => (s ? { ...s, voci } : s))
          }
          toast={notify}
        />
        <ClientePane
          session={session || { cliente: {}, session_id: "" }}
          onGenerateMessage={() => handleGenerateMessage(false)}
          generating={generatingMsg}
          message={session?.quick_message || ""}
          toast={notify}
          onVociUpdate={(voci) =>
            setSession((s) => (s ? { ...s, voci } : s))
          }
        />

        <QuickMessageModal
          open={messageOpen}
          onClose={() => setMessageOpen(false)}
          message={session?.quick_message || ""}
          generating={generatingMsg}
          error={messageError}
          onRegenerate={() => handleGenerateMessage(true)}
          onCopied={() => notify("Copiato negli appunti")}
        />

        {/* R5 — Parameter inference confirmation popup */}
        <NormalizationConfirmModal
          open={!!normQueue.head}
          hint={normQueue.head?.hint || null}
          offerId={session?.session_id}
          voceIdx={normQueue.head?.voceIdx ?? 0}
          voce={normQueue.head?.voce || null}
          onResolved={applyInferenceResolved}
          onDismiss={normQueue.dismiss}
        />
      </div>
    </MobileGate>
  );
}
